import React, { useContext, useEffect, useState } from 'react'
import { DashboardContext } from '../../../context/DashboardContext'
import { EventsWrap } from '../events/eventsWrap'
import { MeetingsFilters } from './filters'
import { Meeting } from './meeting'
import { NoMeetings } from './NoMeetings'
import { getMeetingProvider } from '@/utils/getMeetingProvider'

export function Meetings() {
  const { meetingsOverview, allProjects, allSessions, sessions, setSessions, sessionsLoading, products } =
    useContext(DashboardContext)
  const [projectFilter, setProjectFilter] = useState(-1)
  const [validationFilter, setValidationFilter] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [productFilter, setProductFilter] = useState('')

  function handleProjectFilter(projectIndex) {
    setProjectFilter(projectIndex)
    applyFilters(projectIndex, validationFilter, sourceFilter, productFilter)
  }

  function handleValidationFilter(isProspectToUseIt) {
    setValidationFilter(isProspectToUseIt)
    applyFilters(projectFilter, isProspectToUseIt, sourceFilter, productFilter)
  }

  function handleSourceFilter(source) {
    setSourceFilter(source)
    applyFilters(projectFilter, validationFilter, source, productFilter)
  }

  function handleProductFilter(product) {
    setProductFilter(product)
    applyFilters(projectFilter, validationFilter, sourceFilter, product)
  }

  function applyFilters(projectIdx, validation, source, product) {
    let filteredSessions = [...allSessions]

    // Apply product filter
    if (product !== '') {
      filteredSessions = filteredSessions.filter((meeting) => {
        const project = allProjects.find((p) => p.id === meeting.project_id)
        return project?.product === product
      })
    }

    // Apply project filter
    if (projectIdx !== -1) {
      const projectId = allProjects[projectIdx].id
      filteredSessions = filteredSessions.filter((meeting) => meeting.project_id === projectId)
    }

    // Apply validation filter
    if (validation !== '') {
      filteredSessions = filteredSessions.filter(
        (meeting) => meetingsOverview[meeting.id]?.validation?.is_prospect_to_use_it === validation
      )
    }

    // Apply source filter
    if (source !== '') {
      filteredSessions = filteredSessions.filter((meeting) => {
        // Check if meeting has source field
        if (meeting.source) {
          return meeting.source.toLowerCase() === source.toLowerCase()
        }
        // Fallback to getMeetingProvider if no source field
        if (meeting.meeting_url) {
          const detectedSource = getMeetingProvider(meeting.meeting_url)
          return detectedSource.toLowerCase() === source.toLowerCase()
        }
        // If neither source nor meeting_url, consider it an upload if that's what we're filtering for
        return source.toLowerCase() === 'upload'
      })
    }

    setSessions(filteredSessions)
  }

  useEffect(() => {
    const resetFilters = () => {
      setProjectFilter(-1)
      setValidationFilter('')
      setSourceFilter('')
      setProductFilter('')
    }
    return () => resetFilters()
  }, [])

  return (
    <>
      {!sessionsLoading && allSessions.length === 0 ? (
        <NoMeetings />
      ) : (
        <div className="pr-[28px]">
          <p className="pt-8 text-[#3f424a] text-[26px] font-semibold font-['Manrope'] leading-[29.90px]">Meetings</p>

          <EventsWrap />

          <div className="w-full mb-[15px] flex justify-between items-center">
            <p className="text-[#62708d] text-lg font-bold font-['Manrope'] leading-tight tracking-wide">
              Recent Meetings
            </p>
            <MeetingsFilters
              projects={allProjects}
              projectIndex={projectFilter}
              setProjectIndex={handleProjectFilter}
              validation={validationFilter}
              setValidation={handleValidationFilter}
              source={sourceFilter}
              setSource={handleSourceFilter}
              product={productFilter}
              setProduct={handleProductFilter}
              products={products}
              projectFilterEnabled={true}
            />
          </div>

          {sessionsLoading ? (
            <div className="flex justify-center items-center">
              <svg className="w-40 h-40" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
                <radialGradient id="a1" cx=".66" fx=".66" cy=".3125" fy=".3125" gradientTransform="scale(1.5)">
                  <stop offset="0" stopColor="#FF156D"></stop>
                  <stop offset=".3" stopColor="#FF156D" stopOpacity=".9"></stop>
                  <stop offset=".6" stopColor="#FF156D" stopOpacity=".6"></stop>
                  <stop offset=".8" stopColor="#FF156D" stopOpacity=".3"></stop>
                  <stop offset="1" stopColor="#FF156D" stopOpacity="0"></stop>
                </radialGradient>
                <circle
                  transform-origin="center"
                  fill="none"
                  stroke="url(#a1)"
                  strokeWidth="15"
                  strokeLinecap="round"
                  strokeDasharray="200 1000"
                  strokeDashoffset="0"
                  cx="100"
                  cy="100"
                  r="70"
                >
                  <animateTransform
                    type="rotate"
                    attributeName="transform"
                    calcMode="spline"
                    dur="2"
                    values="360;0"
                    keyTimes="0;1"
                    keySplines="0 0 1 1"
                    repeatCount="indefinite"
                  ></animateTransform>
                </circle>
                <circle
                  transform-origin="center"
                  fill="none"
                  opacity=".2"
                  stroke="#FF156D"
                  strokeWidth="15"
                  strokeLinecap="round"
                  cx="100"
                  cy="100"
                  r="70"
                ></circle>
              </svg>
            </div>
          ) : (
            <div className="flex flex-wrap gap-9">
              {sessions.map((meeting) => (
                <Meeting
                  key={meeting.id}
                  meeting={meeting}
                  validation={meetingsOverview[meeting.id]?.validation?.is_prospect_to_use_it}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}
